<script setup lang="ts">
/**
 * Панель задачи поверх доски и списка — макет 16:470.
 *
 * Задача открывается, не уводя с доски: колонки под панелью остаются на месте
 * вместе со своим скроллом и отбором, и закрытие возвращает ровно тот экран,
 * с которого её открыли. Это основной способ заглянуть в задачу по ходу дня —
 * страница нужна, когда с задачей садятся работать.
 *
 * Ключ задачи живёт в адресе (`?issue=DEV-1421`): такую ссылку пересылают, и
 * она открывает доску с раскрытой панелью, а не голую карточку.
 *
 * Внутри — те же блоки, что на странице: описание, подзадачи, комментарии.
 * Мета здесь строкой, а не колонкой: на 560 пикселях боковая панель съела бы
 * половину ширины. Правится задача на месте, как и на странице, — кроме
 * названия: его правят там, где видно целиком.
 *
 * Блок GitLab здесь тот же, что на странице: в макете панели он стоит между
 * подзадачами и активностью.
 */
import { AppAvatar, AppDrawer } from '@vinprop/design-system'
import { computed, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'

import IssueComments from '@/features/issue/components/IssueComments.vue'
import IssueDescription from '@/features/issue/components/IssueDescription.vue'
import IssueDevInfo from '@/features/issue/components/IssueDevInfo.vue'
import IssueSubtasks from '@/features/issue/components/IssueSubtasks.vue'
import { useIssueStore } from '@/features/issue/store'
import IssueTypeIcon from '@/features/issues/components/IssueTypeIcon.vue'
import PriorityGlyph from '@/features/issues/components/PriorityGlyph.vue'
import StatusBadge from '@/features/issues/components/StatusBadge.vue'
import { formatDueDate } from '@/features/issues/format'

const emit = defineEmits<{ failed: [error: unknown] }>()

const { t, locale } = useI18n()
const route = useRoute()
const router = useRouter()
const store = useIssueStore()

const issueKey = computed(() => String(route.query.issue ?? ''))
const isOpen = computed(() => Boolean(issueKey.value))

const due = computed(() =>
  store.issue ? formatDueDate(store.issue.dueDate, locale.value) : '',
)

/**
 * Закрытие убирает из адреса только ключ задачи: отбор и вид доски лежат в
 * тех же параметрах, и сбрасывать их значило бы наказывать за любопытство.
 */
const close = () => {
  const query = { ...route.query }

  delete query.issue

  void router.replace({ query })
}

watch(
  issueKey,
  async (key) => {
    if (!key) return

    await store.open(key)

    // Ключа нет — незачем держать пустую панель: адрес чинится сам.
    if (store.isMissing) close()
  },
  { immediate: true },
)
</script>

<template>
  <AppDrawer :model-value="isOpen" @update:model-value="close">
    <template #header>
      <div v-if="store.issue" class="peek__head">
        <IssueTypeIcon :type="store.issue.type" />
        <span class="peek__key">{{ store.issue.key }}</span>
        <RouterLink class="peek__full" :to="`/issue/${store.issue.key}`">
          {{ t('issue.peek.open') }}
        </RouterLink>
      </div>
    </template>

    <div v-if="store.issue" class="peek">
      <h2 class="peek__title">{{ store.issue.title }}</h2>

      <div class="peek__meta">
        <StatusBadge :name="store.column?.name ?? '—'" :color="store.column?.color ?? ''" />
        <PriorityGlyph :priority="store.issue.priority" />
        <span v-if="due" class="peek__due">{{ due }}</span>
        <AppAvatar
          v-if="store.issue.assigneeName"
          :name="store.issue.assigneeName"
          :src="store.issue.assigneeAvatar"
          :size="24"
        />
      </div>

      <IssueDescription @failed="emit('failed', $event)" />

      <IssueSubtasks @failed="emit('failed', $event)" />

      <IssueDevInfo @failed="emit('failed', $event)" />

      <div class="peek__section">
        <span class="peek__label">{{ t('issue.peek.activity') }}</span>
        <IssueComments @failed="emit('failed', $event)" />
      </div>
    </div>
  </AppDrawer>
</template>

<!-- Подложка панели без заливки: в макете 16:470 доска под панелью светлая, а
     затемнение гасит ровно то, ради чего панель и открывают — соседние
     карточки. Стиль глобальный, потому что панель уезжает в body телепортом, и
     важный, потому что своё правило дизайн-система пишет из scoped-стилей.
     По уму панели нужен проп «с затемнением или без» — записано в накопитель
     недоделок, чинится вместе с пакетом (DEV-1501). -->
<style>
.app-drawer {
  background-color: transparent !important;
}

/* Шире 1600 панель берёт 640: на таком экране доска под ней не сжимается.
   Медиа-запросом, а не слушателем: ширина панели — вопрос вёрстки. */
@media (min-width: 1600px) {
  .app-drawer__panel {
    width: 640px !important;
  }
}
</style>

<style scoped>
.peek {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.peek__head {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
}

.peek__key {
  color: var(--color-text-muted);
  font-family: var(--font-family-mono);
  font-size: var(--font-size-body-sm);
  line-height: 17px;
}

/* «Открыть страницу» жмётся к крестику: в макете это правый край шапки */
.peek__full {
  margin-left: auto;
  color: var(--color-primary);
  font-size: var(--font-size-body-sm);
  text-decoration: none;
}

.peek__full:hover {
  text-decoration: underline;
}

.peek__title {
  margin: 0;
  color: var(--color-text-primary);
  font-size: var(--font-size-2xl);
  font-weight: var(--font-weight-semibold);
  line-height: 30px;
}

.peek__meta {
  display: flex;
  align-items: center;
  gap: 12px;
}

.peek__due {
  color: var(--color-text-secondary);
  font-family: var(--font-family-mono);
  font-size: var(--font-size-mono);
  font-weight: var(--font-weight-medium);
  line-height: 16px;
}

.peek__section {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding-top: 4px;
  border-top: 1px solid var(--color-border);
}

.peek__label {
  color: var(--color-text-muted);
  font-family: var(--font-family-mono);
  font-size: var(--font-size-overline);
  line-height: 13px;
  text-transform: uppercase;
}
</style>
